<template>
  <div class="hp-wrap">
    <div class="hp-kpis">
      <HrWorkforceKpiTile class="hp-kpi-ring">
        <HrWorkforceDonut :items="ringItems" :total="100" :value="currentProfile.average + '%'" caption="average" :size="92" :hole="70" />
        <div class="hp-kpi-text">
          <span class="hw-label">Overall completion</span>
          <span class="hw-muted">{{ fieldCount }} profile fields</span>
          <span class="hw-muted">{{ selectedBranchName }}</span>
        </div>
      </HrWorkforceKpiTile>
      <HrWorkforceKpiTile label="Employees" :value="onlyCommaFormat(currentProfile.total)" color="#1e293b" :note="selectedBranchName" />
      <HrWorkforceKpiTile
        v-for="status in statusTiles"
        :key="status.key"
        :label="status.label"
        :value="onlyCommaFormat(status.value)"
        :color="status.color"
        :pill="percentLabel(status.value, currentProfile.total)"
        :pill-style="pillStyle(status.key)"
        :note="status.clickable ? status.range : status.range + ' · nothing to fix'"
        :clickable="status.clickable"
        :active="status.clickable && filters.status === status.key"
        :title="status.clickable ? 'List these employees' : ''"
        @select="applyChartFilter('status', status.key)"
      />
    </div>
    <HrWorkforceSubTabs :tabs="subTabs" :model-value="currentSubTab" @update:model-value="activeSubTab = $event" />
    <div v-if="currentSubTab === 'overview'" class="hp-grid">
      <HrWorkforcePanel title="Employees by completion %" note="click a row to list them">
        <HrWorkforceBarRow
          v-for="level in completionHistogram"
          :key="level.key"
          :segments="[{ key: level.key, value: level.value, color: statusMeta[level.status].color }]"
          :scale="maxLevelValue"
          :value="onlyCommaFormat(level.value)"
          :pill="percentLabel(level.value, currentProfile.total)"
          name-width="90px"
          clickable
          :active="filters.filled === level.filled"
          :muted="level.value === 0"
          :disabled="level.value === 0 || level.filled === fieldCount"
          @select="applyChartFilter('filled', level.filled)"
        >
          <template #name><strong>{{ level.percent }}%</strong> <span class="hw-muted">{{ level.filled }}/{{ fieldCount }}</span></template>
        </HrWorkforceBarRow>
      </HrWorkforcePanel>
      <HrWorkforcePanel title="Field coverage" note="click a field to see who is missing it">
        <HrWorkforceBarRow
          v-for="field in fieldCoverage"
          :key="field.key"
          :name="field.label"
          :segments="[{ key: field.key, value: field.filled, color: statusMeta[coverageStatus(field)].color }]"
          :scale="currentProfile.total"
          :pill="percentLabel(field.filled, currentProfile.total)"
          :pill-style="pillStyle(coverageStatus(field))"
          name-width="110px"
          clickable
          :active="filters.missing_field === field.key"
          :disabled="field.missing === 0"
          @select="applyChartFilter('missing_field', field.key)"
        >
          <template #extra>
            <span class="hp-missing-count" :class="{ 'hw-muted': field.missing === 0 }">{{ field.missing === 0 ? 'none missing' : onlyCommaFormat(field.missing) + ' missing' }}</span>
          </template>
        </HrWorkforceBarRow>
      </HrWorkforcePanel>
    </div>
    <HrWorkforcePanel v-if="currentSubTab === 'branches'" title="Completion by branch" note="lowest first · click a branch to drill down">
      <HrWorkforceLegend :items="statusList" />
      <div class="hw-scroll">
        <HrWorkforceBarRow
          v-for="row in branchProfileRows"
          :key="row.key"
          :name="row.name"
          :title="stackTitle(row)"
          :segments="stackParts(row)"
          :scale="row.total"
          stacked
          :value="onlyCommaFormat(row.total)"
          :pill="row.average + '%'"
          :pill-style="pillStyle(averageStatus(row.average))"
          name-width="180px"
          clickable
          @select="selectBranch(row.key)"
        />
      </div>
    </HrWorkforcePanel>
    <HrWorkforcePanel v-if="currentSubTab === 'divisions'" title="Completion by District Fellowship" :note="selectedBranchName + ' · click a row to list them'">
      <HrWorkforceLegend :items="statusList" />
      <div class="hw-scroll">
        <HrWorkforceBarRow
          v-for="row in divisionProfileRows"
          :key="row.key"
          :name="row.name"
          :title="stackTitle(row)"
          :segments="stackParts(row)"
          :scale="row.total"
          stacked
          :value="onlyCommaFormat(row.total)"
          :pill="row.average + '%'"
          :pill-style="pillStyle(averageStatus(row.average))"
          name-width="180px"
          clickable
          :active="filters.division === row.key"
          @select="applyChartFilter('division', row.key)"
        />
      </div>
    </HrWorkforcePanel>
    <HrWorkforcePanel
      v-if="currentSubTab === 'employees'"
      class="hp-employees"
      title="Employees"
      :note="'least complete employees, up to ' + perBranchLimit + ' per branch · as of last Sync'"
      :pager="filteredEmployees.length > 0 ? pager : null"
      @update:page="page = $event"
    >
      <HrWorkforceEmployeeList
        :rows="pagedEmployees"
        :search="searchText"
        :chips="activeChips"
        :capped-note="cappedNote"
        :empty-text="emptyText"
        :action-label="employeePage ? 'Update profile' : ''"
        @search="onSearch"
        @clear="clearChip"
        @clear-all="clearAll"
        @open="openProfile"
      >
        <template #filters>
          <select v-model="filters.status" class="form-select form-select-sm" aria-label="Status" @change="onStatusChange">
            <option value="">All statuses ({{ onlyCommaFormat(attentionRows.length) }})</option>
            <option v-for="status in listStatuses" :key="status.key" :value="status.key">{{ status.label }} ({{ onlyCommaFormat(status.value) }})</option>
          </select>
          <select v-model="filters.filled" class="form-select form-select-sm" aria-label="Completion" @change="onFilledChange">
            <option value="">Any completion</option>
            <option v-for="level in listLevels" :key="level.filled" :value="level.filled">{{ level.percent }}% · {{ level.filled }}/{{ fieldCount }} ({{ onlyCommaFormat(level.value) }})</option>
          </select>
          <select v-model="filters.missing_field" class="form-select form-select-sm" aria-label="Missing field" @change="page = 1">
            <option value="">Any missing field</option>
            <option v-for="field in listFields" :key="field.key" :value="field.key">Missing {{ field.label }} ({{ onlyCommaFormat(field.value) }})</option>
          </select>
          <select v-if="listDivisions.length > 0" v-model="filters.division" class="form-select form-select-sm" aria-label="District Fellowship" @change="page = 1">
            <option value="">Any District Fellowship</option>
            <option v-for="division in listDivisions" :key="division.key" :value="division.key">{{ division.name }} ({{ onlyCommaFormat(division.value) }})</option>
          </select>
        </template>
        <template #default="{ employee }">
          <div class="hp-emp-progress">
            <div class="hp-progress-head">
              <strong :style="{ color: statusMeta[employee.status].text }">{{ employee.percent }}%</strong>
              <span class="hw-muted">{{ employee.filled }}/{{ fieldCount }} fields</span>
            </div>
            <div class="hp-progress">
              <span :style="{ width: employee.percent + '%', backgroundColor: statusMeta[employee.status].color }"></span>
            </div>
            <div class="hp-dots">
              <span
                v-for="check in employee.checks"
                :key="check.key"
                class="hp-check"
                :class="check.filled ? 'hp-check-ok' : 'hp-check-miss'"
                :title="fieldLabels[check.key] + (check.filled ? ': filled' : ': missing')"
              ></span>
            </div>
          </div>
          <div class="hp-emp-missing">
            <span v-for="label in employee.missing_labels" :key="label" class="hp-chip hp-chip-miss">{{ label }}</span>
          </div>
        </template>
      </HrWorkforceEmployeeList>
      <template #pager-note>
        <span v-if="isCapped"> · <strong class="hw-true-total">{{ onlyCommaFormat(trueMatchTotal) }}</strong> match in total</span>
      </template>
    </HrWorkforcePanel>
  </div>
</template>

<script setup>
import { computed, reactive, watch } from 'vue'
import figureFormatter from '@/services/utils/figureFormatter'
import { matchesSearch, notAssignedKey, notSpecifiedKey, percentLabel, profileStatusOf, useHrWorkforceEmployeeFilters, useHrWorkforceEmployeeProfile, useHrWorkforcePagedRows } from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/hrWorkforce'
import HrWorkforcePanel from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforcePanel.vue'
import HrWorkforceKpiTile from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforceKpiTile.vue'
import HrWorkforceDonut from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforceDonut.vue'
import HrWorkforceSubTabs from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforceSubTabs.vue'
import HrWorkforceLegend from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforceLegend.vue'
import HrWorkforceBarRow from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforceBarRow.vue'
import HrWorkforceEmployeeList from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforceEmployeeList.vue'

const props = defineProps({
  current: { type: Object, required: true },
  labels: { type: Object, required: true },
  branchRows: { type: Array, required: true },
  selectedBranch: { type: String, required: true },
  selectedBranchName: { type: String, required: true },
  attentionEmployees: { type: Object, default: () => ({}) }
})

const emit = defineEmits(['select-branch'])

const statusMeta = {
  complete: { label: 'Complete', color: '#1baf7a', text: '#0f7a55', tint: '#e7f7f0' },
  partial: { label: 'Partial', color: '#eda100', text: '#8a5a00', tint: '#fdf3dc' },
  incomplete: { label: 'Incomplete', color: '#e34948', text: '#b42318', tint: '#fdeaea' }
}
const statusOrder = ['complete', 'partial', 'incomplete']
const perBranchLimit = 10
const pageSize = 10

const { onlyCommaFormat } = figureFormatter()
const { employeePage, openProfile } = useHrWorkforceEmployeeProfile()

const filters = reactive({ status: '', filled: '', missing_field: '', division: '', search: '' })
const { activeSubTab, searchText, page, applyChartFilter, selectBranch, clearChip, clearAll, onSearch } = useHrWorkforceEmployeeFilters(filters, props, emit)

const fieldLabels = computed(() => props.labels.profile_fields || {})
const fieldKeys = computed(() => Object.keys(fieldLabels.value))
const fieldCount = computed(() => fieldKeys.value.length)
const partialFrom = computed(() => Math.ceil(fieldCount.value / 2))
const levels = computed(() => Array.from({ length: fieldCount.value + 1 }, (item, index) => fieldCount.value - index))
const currentProfile = computed(() => profileSummary(props.current.profile))
const ringItems = computed(() => [
  { key: 'filled', value: currentProfile.value.average, color: statusMeta[averageStatus(currentProfile.value.average)].color },
  { key: 'remaining', value: 100 - currentProfile.value.average, color: '#e2e8f0' }
])

const statusRanges = computed(() => ({
  complete: fieldCount.value + '/' + fieldCount.value + ' fields',
  partial: partialFrom.value + '–' + (fieldCount.value - 1) + ' fields',
  incomplete: '0–' + (partialFrom.value - 1) + ' fields'
}))

const statusList = computed(() => statusOrder.map(key => ({ key, ...statusMeta[key] })))

const statusTiles = computed(() => [...statusOrder].reverse().map(key => ({
  key,
  label: statusMeta[key].label,
  color: statusMeta[key].color,
  value: currentProfile.value[key],
  range: statusRanges.value[key],
  clickable: key !== 'complete'
})))

const completionHistogram = computed(() => levels.value.map(filled => ({
  key: 'f_' + filled,
  filled,
  percent: Math.round(filled * 100 / fieldCount.value),
  status: statusOf(filled),
  value: (props.current.profile || {})['f_' + filled] || 0
})))

const maxLevelValue = computed(() => Math.max(1, ...completionHistogram.value.map(level => level.value)))

const fieldCoverage = computed(() => {
  const filledCounts = props.current.profile_fields || {}
  return fieldKeys.value.map(key => ({
    key,
    label: fieldLabels.value[key],
    filled: filledCounts[key] || 0,
    missing: currentProfile.value.total - (filledCounts[key] || 0)
  })).sort((a, b) => a.filled - b.filled)
})

const branchProfileRows = computed(() => props.branchRows
  .map(row => ({ key: row.key, name: row.name, ...profileSummary(row.summary.profile) }))
  .filter(row => row.total > 0)
  .sort((a, b) => (a.key === notAssignedKey) - (b.key === notAssignedKey) || a.average - b.average || b.total - a.total))

const divisionProfileRows = computed(() => {
  const counts = props.current.division_profile || {}
  const divisionLabels = props.labels.divisions || {}
  const grouped = Object.keys(counts).reduce((result, key) => {
    const [divisionKey, levelKey] = key.split('|')
    result[divisionKey] = { ...(result[divisionKey] || {}), [levelKey]: counts[key] }
    return result
  }, {})
  return Object.keys(grouped)
    .map(key => ({ key, name: divisionLabels[key] || 'Not specified', ...profileSummary(grouped[key]) }))
    .sort((a, b) => (a.key === notSpecifiedKey) - (b.key === notSpecifiedKey) || a.average - b.average || b.total - a.total)
})

const hasDivisionData = computed(() => divisionProfileRows.value.some(row => row.key !== notSpecifiedKey))

const subTabs = computed(() => [
  { key: 'overview', title: 'Overview', count: null, visible: true },
  { key: 'branches', title: 'By branch', count: branchProfileRows.value.length, visible: props.selectedBranch === 'all' },
  { key: 'divisions', title: 'By District Fellowship', count: null, visible: hasDivisionData.value },
  { key: 'employees', title: 'Employees', count: filteredEmployees.value.length, visible: true }
].filter(tab => tab.visible))

const currentSubTab = computed(() => subTabs.value.some(tab => tab.key === activeSubTab.value) ? activeSubTab.value : subTabs.value[0].key)

const attentionRows = computed(() => {
  const map = props.attentionEmployees || {}
  const branchKeys = props.selectedBranch === 'all' ? Object.keys(map) : [props.selectedBranch]
  return branchKeys
    .flatMap(branchKey => (map[branchKey] || []).map(row => toEmployee(row, branchKey)))
    .sort((a, b) => a.filled - b.filled || String(a.name).localeCompare(String(b.name)))
})

const filteredEmployees = computed(() => {
  const search = filters.search.toLowerCase()
  return attentionRows.value.filter(row => matchesFilters(row, search))
})

const listStatuses = computed(() => ['incomplete', 'partial'].map(key => ({
  key,
  label: statusMeta[key].label,
  value: attentionRows.value.filter(row => row.status === key).length
})))

const listLevels = computed(() => levels.value
  .filter(filled => filled < fieldCount.value)
  .map(filled => ({ filled, percent: Math.round(filled * 100 / fieldCount.value), value: attentionRows.value.filter(row => row.filled === filled).length }))
  .filter(level => level.value > 0 || filters.filled === level.filled))

const listFields = computed(() => fieldKeys.value
  .map(key => ({ key, label: fieldLabels.value[key], value: attentionRows.value.filter(row => row.missing.includes(key)).length }))
  .filter(field => field.value > 0 || filters.missing_field === field.key)
  .sort((a, b) => b.value - a.value))

const listDivisions = computed(() => {
  const counts = attentionRows.value.reduce((result, row) => ({ ...result, [row.division]: (result[row.division] || 0) + 1 }), {})
  return Object.keys(counts)
    .map(key => ({ key, name: divisionName(key), value: counts[key] }))
    .sort((a, b) => (a.key === notSpecifiedKey) - (b.key === notSpecifiedKey) || a.name.localeCompare(b.name))
})

const trueMatchTotal = computed(() => {
  const active = ['status', 'filled', 'missing_field', 'division'].filter(key => filters[key] !== '')
  if (filters.search !== '' || active.length > 1) return null
  if (active.length === 0) return currentProfile.value.total - currentProfile.value.complete
  return singleFilterTotal(active[0])
})

const isCapped = computed(() => trueMatchTotal.value !== null && trueMatchTotal.value > filteredEmployees.value.length)

const { pagedRows: pagedEmployees, pager } = useHrWorkforcePagedRows(filteredEmployees, page, pageSize)

const cappedNote = computed(() => {
  if (!isCapped.value) return ''
  return onlyCommaFormat(trueMatchTotal.value) + ' employees match in total. Only the ' + perBranchLimit + ' least complete per branch are kept at Sync, so ' + onlyCommaFormat(trueMatchTotal.value - filteredEmployees.value.length) + ' more are not listed.'
})

const emptyText = computed(() => {
  if (attentionRows.value.length === 0) return "Everyone's profile is complete 🎉"
  if (filteredEmployees.value.length === 0) return 'No employees match these filters.'
  return ''
})

const activeChips = computed(() => {
  const chips = []
  if (props.selectedBranch !== 'all') chips.push({ key: 'branch', label: 'Branch: ' + props.selectedBranchName })
  if (filters.status !== '') chips.push({ key: 'status', label: 'Status: ' + statusMeta[filters.status].label })
  if (filters.filled !== '') chips.push({ key: 'filled', label: 'Completion: ' + Math.round(filters.filled * 100 / fieldCount.value) + '%' })
  if (filters.missing_field !== '') chips.push({ key: 'missing_field', label: 'Missing: ' + fieldLabels.value[filters.missing_field] })
  if (filters.division !== '') chips.push({ key: 'division', label: 'District Fellowship: ' + divisionName(filters.division) })
  if (filters.search !== '') chips.push({ key: 'search', label: 'Search: ' + filters.search })
  return chips
})

const statusOf = (filled) => profileStatusOf(filled, fieldCount.value)

const averageStatus = (average) => statusOf(Math.floor(average * fieldCount.value / 100))

const coverageStatus = (field) => averageStatus(currentProfile.value.total > 0 ? Math.round(field.filled * 100 / currentProfile.value.total) : 0)

const profileSummary = (counts) => {
  const safeCounts = counts || {}
  const rows = levels.value.map(filled => ({ filled, value: safeCounts['f_' + filled] || 0 }))
  const total = rows.reduce((sum, row) => sum + row.value, 0)
  const filledSum = rows.reduce((sum, row) => sum + row.value * row.filled, 0)
  const statusTotal = (status) => rows.filter(row => statusOf(row.filled) === status).reduce((sum, row) => sum + row.value, 0)
  return {
    total,
    average: total > 0 ? Math.round(filledSum * 100 / (total * fieldCount.value)) : 0,
    complete: statusTotal('complete'),
    partial: statusTotal('partial'),
    incomplete: statusTotal('incomplete')
  }
}

const divisionName = (key) => (props.labels.divisions || {})[key] || 'Not specified'

const stackParts = (row) => statusOrder.map(key => ({ key, value: row[key], color: statusMeta[key].color })).filter(part => part.value > 0)

const stackTitle = (row) => row.name + ' · ' + statusOrder.map(key => statusMeta[key].label + ' ' + row[key]).join(' · ') + ' · average ' + row.average + '%'

const pillStyle = (status) => ({ backgroundColor: statusMeta[status].tint, color: statusMeta[status].text })

const toEmployee = (row, branchKey) => {
  const missing = row.missing || []
  const filled = fieldCount.value - missing.length
  const status = statusOf(filled)
  const branchName = branchKey === notAssignedKey ? '' : (props.labels.branches || {})[branchKey] || ''
  const divisionLabel = row.division === notSpecifiedKey ? '' : divisionName(row.division)
  return {
    ...row,
    missing,
    filled,
    percent: Math.round(filled * 100 / fieldCount.value),
    status,
    checks: fieldKeys.value.map(key => ({ key, filled: !missing.includes(key) })),
    missing_labels: missing.map(key => fieldLabels.value[key] || key),
    subtitle: (row.serial ? 'ID ' + row.serial + ' · ' : '') + (branchName || 'No branch') + ' · ' + (divisionLabel || 'No division'),
    avatar_style: pillStyle(status)
  }
}

const matchesFilters = (row, search) => {
  if (filters.status !== '' && row.status !== filters.status) return false
  if (filters.filled !== '' && row.filled !== filters.filled) return false
  if (filters.missing_field !== '' && !row.missing.includes(filters.missing_field)) return false
  if (filters.division !== '' && row.division !== filters.division) return false
  return matchesSearch(row, search)
}

const singleFilterTotal = (key) => {
  if (key === 'status') return currentProfile.value[filters.status]
  if (key === 'filled') return (props.current.profile || {})['f_' + filters.filled] || 0
  if (key === 'missing_field') return currentProfile.value.total - ((props.current.profile_fields || {})[filters.missing_field] || 0)
  const counts = props.current.division_profile || {}
  return levels.value.filter(filled => filled < fieldCount.value).reduce((sum, filled) => sum + (counts[filters.division + '|f_' + filled] || 0), 0)
}

const onStatusChange = () => {
  filters.filled = ''
  page.value = 1
}

const onFilledChange = () => {
  filters.status = ''
  page.value = 1
}

watch(() => props.selectedBranch, () => {
  if (filters.division === '') return
  if (attentionRows.value.some(row => row.division === filters.division)) return
  filters.division = ''
})
</script>

<style scoped src="./common/hrWorkforce.css"></style>

<style scoped>
.hp-wrap {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.hp-kpis {
  display: grid;
  grid-template-columns: minmax(250px, 1.4fr) repeat(4, minmax(140px, 1fr));
  gap: 14px;
}
.hp-kpis .hp-kpi-ring {
  flex-direction: row;
  align-items: center;
  gap: 16px;
}
.hp-kpi-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.hp-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}
.hp-missing-count {
  min-width: 96px;
  text-align: right;
  font-size: 0.78rem;
  font-weight: 600;
  color: #b42318;
  white-space: nowrap;
}
.hp-employees {
  --hw-filter-cols: minmax(200px, 1.4fr) repeat(4, minmax(140px, 1fr));
  --hw-emp-cols: minmax(0, 1.3fr) minmax(170px, 1fr) minmax(0, 1.5fr) auto;
}
.hp-progress-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 0.85rem;
  margin-bottom: 4px;
}
.hp-progress {
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background: #f1f5f9;
}
.hp-progress span {
  display: block;
  height: 100%;
  border-radius: 4px;
}
.hp-dots {
  display: flex;
  gap: 3px;
  margin-top: 6px;
}
.hp-check {
  flex: 1;
  height: 4px;
  border-radius: 2px;
}
.hp-check-ok {
  background: #1baf7a;
}
.hp-check-miss {
  background: #fecaca;
}
.hp-emp-missing {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  min-width: 0;
}
.hp-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}
.hp-chip-miss {
  background: #fdeaea;
  color: #b42318;
}
@media (max-width: 1199px) {
  .hp-kpis {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .hp-kpi-ring {
    grid-column: span 2;
  }
  .hp-employees {
    --hw-emp-cols: minmax(0, 1fr) minmax(170px, 1fr) auto;
  }
  .hp-emp-missing {
    grid-column: 1 / -1;
    grid-row: 2;
  }
}
@media (max-width: 991px) {
  .hp-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
@media (max-width: 575px) {
  .hp-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .hp-kpi-ring {
    grid-column: 1 / -1;
  }
  .hp-emp-missing {
    grid-row: auto;
  }
}
</style>
